import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import { flattenDeep } from 'lodash';

import { RCAccordion, RCCheckbox } from '@reverbdotcom/cadence/components';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import CollapsingList from '@reverbdotcom/commons/src/components/collapsing_list';
import CollapsingFacet from '@reverbdotcom/commons/src/components/collapsing_facet';
import { PROXIMITY_PARAM } from '@reverbdotcom/commons/src/components/proximity_facet';
import { SearchRemixer_FacetFieldsFragment, SearchRemixer_FacetGroupFieldsFragment } from '@reverbdotcom/commons/src/gql/graphql';

import ProximityFacetOption from './facets/proximity_facet_option';
import RangeFacet from './facets/range_facet';
import SearchTermFacet from './facets/search_term_facet';
import { SidebarRegionSelectFacet, RegionSelectFacet } from './facets/region_select_facet';

export const SHIPS_TO = 'ships_to';

function hasSelectedChild(facet: SearchRemixer_FacetFieldsFragment) {
  if (!facet.subFacets) return false;
  return facet.subFacets?.some(sf => sf.selected);
}

export function isSomethingSelected(facet: SearchRemixer_FacetFieldsFragment, excludeAll = false) {
  return !(excludeAll && facet.all) && (facet.selected || hasSelectedChild(facet));
}

function getSelectedOptionsForFacet(facet: SearchRemixer_FacetFieldsFragment, acc = []) {
  if (facet.selected && !facet.all) {
    acc.push(facet);
  }
  if (facet?.subFacets) {
    facet.subFacets.forEach(subFacet => getSelectedOptionsForFacet(subFacet, acc));
  }
  return acc;
}

export function allSelectedFacets(facetGroups: SearchRemixer_FacetGroupFieldsFragment[]) {
  const allFacets = flattenDeep(facetGroups.map(fg => fg.facets || []));
  const allSelected = flattenDeep(allFacets.map(facet => getSelectedOptionsForFacet(facet, [])));
  return allSelected;
}

export const FACET_MAPPING = {
  'CHECKBOX': CheckboxSelectFacet,
  'NESTED_SELECT': NestedSelectFacet,
  'RANGE': RangeFacet,
  'REGION_SELECT': RegionSelectFacet,
};

export const SIDEBAR_FACET_MAPPING = {
  'CHECKBOX': CheckboxSelectFacet,
  'NESTED_SELECT': SidebarNestedSelectFacet,
  'REGION_SELECT': SidebarRegionSelectFacet,
  'TEXT': SearchTermFacet,
};

export function SidebarNestedSelectFacet({ facetGroup, disabled = false }: { facetGroup: SearchRemixer_FacetGroupFieldsFragment, disabled?: boolean }) {
  return (
    <div className="facet facet--collapsible" data-testid={`nested-facet-group_${facetGroup.displayName}`}>
      <RCAccordion
        id={facetGroup.key}
        heading={facetGroup.displayName}
        defaultOpen={facetGroup.featured > -1}
      >
        <ul className="facet__options">
          {
            facetGroup.facets.map((facet) => {
              return <SelectFacetOption facet={facet} key={`${facetGroup.displayName}-${facet.displayName}`} disabled={disabled} />;
            })
          }
        </ul>
      </RCAccordion>
    </div>
  );
}

export function NestedSelectFacet({ facetGroup }) {
  const allFacet = facetGroup?.facets.find(f => f.all);
  const somethingSelected = facetGroup?.facets.filter(f => isSomethingSelected(f, true));
  const baseFacets = somethingSelected.length > 0 ? somethingSelected : facetGroup.facets;
  const facets = allFacet && !baseFacets.includes(allFacet) ? [allFacet, ...baseFacets] : baseFacets;

  return (
    <div className="facet" data-testid={`nested-facet-group_${facetGroup.displayName}`}>
      <h3 className="facet__heading">{facetGroup.displayName}</h3>
      <ul className="facet__options">
        {
          facets.map((facet) => {
            return <SelectFacetOption facet={facet} key={`${facetGroup.displayName}-${facet.displayName}`} />;
          })
        }
      </ul>
    </div>
  );
}

function SelectFacetOption({ facet, disabled = false }) {
  const classes = classNames(`facet__option facet__option--${facet.key}`, {
    'facet__option--active ': facet.selected,
    'facet__option--child-active': hasSelectedChild(facet),
  });

  return (
    <div className={classes}>
      <CoreLink
        to={facet.searchUrl}
        className={facet.selected ? 'facet__link facet__link--active' : 'facet__link'}
        key={facet.displayName}
        disabled={disabled}
      >
        <div>
          <span className="mr-space">{facet.displayName}</span>
          <FacetCount facet={facet} />
        </div>
      </CoreLink>
      {isSomethingSelected(facet) && facet.subFacets &&
        <CollapsingFacet count={facet.subFacets.length} countThreshold={6}>
          <ul className="facet__options">
            {
              facet.subFacets.map(subFacet => {
                return <SelectFacetOption facet={subFacet} key={`${facet.displayName}-${subFacet.displayName}`} />;
              })
            }
          </ul>
        </CollapsingFacet>
      }
    </div>
  );
}

interface CheckboxFilterProps {
  facetGroup: SearchRemixer_FacetGroupFieldsFragment;
  sidebar?: boolean;
  disabled?: boolean;
}

export function CheckboxSelectFacet({ facetGroup, sidebar = false, disabled = false }: CheckboxFilterProps) {
  const classes = classNames('facet', { 'facet--collapsible': sidebar });

  return (
    <div className={classes} data-testid={`checkbox-facet-group_${facetGroup.displayName}`}>
      <RCAccordion
        id={facetGroup.key}
        heading={facetGroup.displayName}
        defaultOpen={sidebar ? (facetGroup.featured > -1 || facetGroup.facets.some(f => isSomethingSelected(f, true))) : true}
        zeroPadding={!sidebar}
        locked={!sidebar}
      >
        <CollapsingList renderInFilterList>
          {
            facetGroup.facets.map(facet => {
              return facet.paramValue === PROXIMITY_PARAM ?
                <ProximityFacetOption
                  facet={facet}
                  disabled={disabled}
                  key={`${facet.displayName}-${facet.selected}`}
                /> : (
                  <RCCheckbox
                    id={facet.displayName}
                    key={`${facet.displayName}-${facet.selected}`} // force a re-render of the checkbox when the selected state changes
                    checked={facet.selected}
                    paddedHover
                    disabled={disabled}
                    linkElement={(children: React.ReactNode) => (
                      <CoreLink to={facet.searchUrl} rel="nofollow" >
                        {children}
                      </CoreLink>
                    )}
                    label={
                      <div>
                        <span className="mr-space">{facet.displayName}</span>
                        <FacetCount facet={facet} />
                      </div>
                    }
                  />
                );
            })
          }
        </CollapsingList>
      </RCAccordion>
    </div>
  );
}

export function FacetCount({ facet }: { facet: SearchRemixer_FacetFieldsFragment }) {
  if (!facet.count) { return null; }

  return (
    <span className="color-secondary weight-normal">
      ({I18n.toNumber(facet.count, { precision: 0 })})
    </span>
  );
}
